import { Skeleton } from '@/components/ui/skeleton';

/** Rows in a loading table — a hint of the list, not a full page of grey. */
export const SKELETON_ROWS = 4;

/** Checkbox, Account, then four value columns and the pinned ⋮ — the default view's rhythm. */
export const SKELETON_COLS = '18px minmax(220px, 2.4fr) repeat(4, minmax(96px, 1fr)) 28px';

/**
 * The accounts table's loading rows, shaped like real rows (checkbox, avatar +
 * address, status pill, text, deliverability %, chip, ⋮) on the default
 * view's column rhythm — the first page load (`EmailAccountsPendingState`),
 * before the user's column preferences are known. A page / page-size / sort /
 * filter / search change uses `tanstack/EmailAccountsColumnSkeletonRows`
 * instead: the same shapes, laid out on the table's real visible columns.
 */
export function EmailAccountsSkeletonRows({ rows = SKELETON_ROWS }: { rows?: number }) {
  return (
    <div role="rowgroup">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          role="row"
          className="grid min-h-[58px] items-center gap-6 border-b px-[18px]"
          style={{ gridTemplateColumns: SKELETON_COLS, borderColor: 'var(--ink-100)' }}
        >
          <div role="cell">
            <Skeleton className="size-4 rounded" />
          </div>
          <div role="cell" className="flex min-w-0 items-center gap-3">
            <Skeleton className="h-4 w-8 shrink-0 rounded-full" />
            <Skeleton className="size-[30px] shrink-0 rounded-[9px]" />
            <Skeleton className="h-3.5" style={{ width: `${[62, 48, 70, 55][i % 4]}%` }} />
          </div>
          <div role="cell">
            <Skeleton className="h-[22px] w-20 rounded-full" />
          </div>
          <div role="cell">
            <Skeleton className="h-3.5 w-24" />
          </div>
          {/* Deliverability is plain text now ("92%"), not a ring. */}
          <div role="cell">
            <Skeleton className="h-3.5 w-10" />
          </div>
          <div role="cell">
            <Skeleton className="h-5 w-14 rounded-md" />
          </div>
          <div role="cell" className="justify-self-center">
            <Skeleton className="size-5 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}
